/**
 * 统一鼠标交互 Hook
 * 处理单击选中、双击编辑、鼠标悬停等鼠标操作
 */

import { useEffect, useRef, useCallback } from 'react';
import { UnifiedPosition, CurrentStateAndPosition } from './useUnifiedNavigationState';
import { useUnifiedFieldEdit } from './useUnifiedFieldEdit';

interface UseUnifiedMouseInteractionOptions {
  enabled?: boolean;
  setSelection: (position: UnifiedPosition | null) => void;
  setEditing: (position: UnifiedPosition | null) => void;
  setHover: (position: UnifiedPosition | null) => void;
  currentStateAndPosition: CurrentStateAndPosition | null;
  scenesTableElementRefs: Map<string, HTMLElement>;
  sceneDetailsElementRefs: Map<string, HTMLElement>;
  elementTableElementRefs: Map<string, HTMLElement>;
  getPositionFromElement: (element: HTMLElement) => UnifiedPosition | null;
}

/**
 * 从 CurrentStateAndPosition 提取 UnifiedPosition
 */
function extractPosition(stateAndPosition: CurrentStateAndPosition): UnifiedPosition | null {
  if (!stateAndPosition) return null;
  const { state, ...position } = stateAndPosition;
  return position as UnifiedPosition;
}

/**
 * 检查位置是否处于编辑状态
 */
function isPositionEditing(
  position: UnifiedPosition,
  currentStateAndPosition: CurrentStateAndPosition | null
): boolean {
  if (!currentStateAndPosition || currentStateAndPosition.state !== 'editing') {
    return false;
  }
  const currentPos = extractPosition(currentStateAndPosition);
  if (!currentPos) return false;
  
  // 比较位置是否相同
  if (currentPos.type !== position.type) return false;
  
  switch (position.type) {
    case 'scenesTable':
      return currentPos.type === 'scenesTable' &&
        currentPos.sceneID === position.sceneID &&
        currentPos.row === position.row &&
        currentPos.col === position.col;
    case 'sceneDetails':
      return currentPos.type === 'sceneDetails' &&
        currentPos.sceneID === position.sceneID &&
        currentPos.field === position.field;
    case 'elementTable':
      return currentPos.type === 'elementTable' &&
        currentPos.elementID === position.elementID &&
        currentPos.field === position.field;
    default:
      return false;
  }
}

export function useUnifiedMouseInteraction(options: UseUnifiedMouseInteractionOptions) {
  const {
    enabled = true,
    setSelection,
    setEditing,
    setHover,
    currentStateAndPosition,
    scenesTableElementRefs,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    sceneDetailsElementRefs: _sceneDetailsElementRefs, // 保留接口兼容性，但当前未使用
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    elementTableElementRefs: _elementTableElementRefs, // 保留接口兼容性，但当前未使用
    getPositionFromElement,
  } = options;

  const { activateFieldEdit } = useUnifiedFieldEdit();
  const clickTimerRef = useRef<NodeJS.Timeout | null>(null);
  const lastClickTimeRef = useRef<number>(0);
  const DOUBLE_CLICK_DELAY = 300;
  const mousedownTargetRef = useRef<Element | null>(null);
  
  // 使用 useRef 存储最新的 currentStateAndPosition，避免闭包问题
  const currentStateAndPositionRef = useRef<CurrentStateAndPosition | null>(currentStateAndPosition);
  
  // 同步 currentStateAndPosition 到 ref
  useEffect(() => {
    currentStateAndPositionRef.current = currentStateAndPosition;
  }, [currentStateAndPosition]);

  // 处理鼠标按下事件（在捕获阶段拦截，阻止输入框聚焦和下拉菜单打开）
  const handleMouseDown = useCallback((e: MouseEvent) => {
    const target = e.target;
    if (!target || !(target instanceof Element)) return;

    // ElementSearch 有独立的编辑状态管理，不参与统一导航，需排除以免阻止文本选择/光标插入
    if ((target as HTMLElement).closest('.element-search')) {
      return;
    }

    // 检查是否是双击（detail === 2）
    if (e.detail === 2) {
      // 阻止默认的文本选择行为
      e.preventDefault();
      return;
    }

    // 检查目标元素是否是输入框或下拉按钮
    const isInput = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA';
    const isDropdownButton = target.classList.contains('scene-detail-select-button') ||
      target.closest('.scene-detail-select-button');

    if (isInput || isDropdownButton) {
      // 获取位置
      const position = getPositionFromElement(target as HTMLElement);
      if (position) {
        // 使用 ref 获取最新的状态，避免闭包问题
        const latestState = currentStateAndPositionRef.current;
        
        // 检查是否已经在编辑状态
        // 编辑状态下：不阻止聚焦，让输入框正常处理
        // 非编辑状态：阻止默认聚焦和下拉菜单打开
        const isEditing = isPositionEditing(position, latestState) ||
          target.classList.contains('editing') ||
          (target as HTMLElement).closest('.editing');
        
        if (!isEditing) {
          e.preventDefault();
          e.stopPropagation();
          mousedownTargetRef.current = target;
        }
        // 编辑状态下不阻止，让输入框正常处理
      }
    }
  }, [getPositionFromElement]);

  // 处理单击事件
  const handleClick = useCallback((e: MouseEvent) => {
    const target = e.target;
    if (!target || !(target instanceof Element)) {
      return;
    }

    // ElementSearch 有独立的编辑状态管理，不参与统一导航，需排除以免点击输入框时被误 blur
    if ((target as HTMLElement).closest('.element-search')) {
      return;
    }

    // 检查是否是双击（通过时间间隔判断）
    const now = Date.now();
    const timeSinceLastClick = now - lastClickTimeRef.current;
    
    if (timeSinceLastClick < DOUBLE_CLICK_DELAY) {
      if (clickTimerRef.current) {
        clearTimeout(clickTimerRef.current);
        clickTimerRef.current = null;
      }
      return;
    }

    // 获取位置
    const position = getPositionFromElement(target as HTMLElement);

    if (!position) {
      return;
    }

    // 使用 ref 获取最新的状态，避免闭包问题
    const latestState = currentStateAndPositionRef.current;
    
    // 检查当前位置是否处于编辑状态
    // 编辑状态下：不处理，让输入框自己处理（插入光标、选中文本等）
    const isEditing = isPositionEditing(position, latestState);

    if (isEditing) {
      return;
    }

    // 清除之前的定时器
    if (clickTimerRef.current) {
      clearTimeout(clickTimerRef.current);
    }

    // 普通/悬停/选中状态：设置选中状态
    // 注意：setSelection 内部会自动清除之前的编辑或选中状态（互斥），不需要额外调用 setEditing(null)
    setSelection(position);

    // 如果输入框或下拉按钮获得了焦点，尝试失焦（非编辑状态）
    const activeElement = document.activeElement;
    if (activeElement && (activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA' ||
      activeElement.classList.contains('scene-detail-select-button'))) {
      (activeElement as HTMLElement).blur();
    }

    // 设置定时器，用于检测双击
    clickTimerRef.current = setTimeout(() => {
      clickTimerRef.current = null;
    }, DOUBLE_CLICK_DELAY);

    lastClickTimeRef.current = now;
  }, [getPositionFromElement, setSelection]);

  // 处理双击事件
  const handleDoubleClick = useCallback((e: MouseEvent) => {
    const target = e.target;
    if (!target || !(target instanceof Element)) return;

    // ElementSearch 有独立的编辑状态管理，不参与统一导航，需排除
    if ((target as HTMLElement).closest('.element-search')) {
      return;
    }

    // 获取位置
    const position = getPositionFromElement(target as HTMLElement);
    if (!position) return;

    // 使用 ref 获取最新的状态，避免闭包问题
    const latestState = currentStateAndPositionRef.current;
    
    // 检查当前位置是否处于编辑状态
    const isEditing = isPositionEditing(position, latestState);
    
    if (isEditing) {
      // 编辑状态下：让浏览器默认处理（选中单词）
      // 不阻止默认行为，不设置状态
      return;
    }

    // 非编辑状态：阻止默认行为，进入编辑状态
    e.preventDefault();
    e.stopPropagation();

    // 清除单击定时器
    if (clickTimerRef.current) {
      clearTimeout(clickTimerRef.current);
      clickTimerRef.current = null;
    }

    // 设置编辑状态
    // 注意：setEditing 内部会自动清除之前的编辑或选中状态（互斥），不需要额外调用 setSelection(null)
    setEditing(position);

    // 如果是输入框，计算双击位置并定位光标
    if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
      const input = target as HTMLInputElement;
      const rect = input.getBoundingClientRect();
      const clickX = e.clientX - rect.left;

      // 检查是否有 onEnterEditing 回调（通过元素属性）
      const onEnterEditing = (input as any).__onEnterEditing;
      if (onEnterEditing && typeof onEnterEditing === 'function') {
        // 先调用 onEnterEditing 回调（用于初始化本地输入值等）
        onEnterEditing(input);
      }

      // 使用 activateFieldEdit 激活编辑，并传递 clickX
      activateFieldEdit({
        element: input,
        autoFocus: true,
        selectText: false,
        clickX,
      });
    } else {
      // 如果不是输入框（可能是 td 或其他元素），需要等待输入框渲染完成
      // 对于 scenesTable，输入框只在 isEditing 为 true 时才渲染
      if (position.type === 'scenesTable') {
        // 等待输入框渲染完成
        // 使用双重 requestAnimationFrame + setTimeout 确保 DOM 更新完成
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            // 使用 setTimeout 再等待一个事件循环，确保 React 完成渲染
            setTimeout(() => {
              // 从 refs 中获取输入框元素
              const key = `${position.row}-${position.col}`;
              let inputElement: HTMLElement | undefined = scenesTableElementRefs.get(key);
              
              // 如果 refs 中还没有，尝试从 DOM 中查找
              if (!inputElement) {
                const td = (target as HTMLElement).closest('td');
                if (td) {
                  const foundInput = td.querySelector('input');
                  inputElement = foundInput || undefined;
                }
              }
              
              if (inputElement && (inputElement.tagName === 'INPUT' || inputElement.tagName === 'TEXTAREA')) {
                const input = inputElement as HTMLInputElement;
                
                // 计算相对于输入框的 clickX
                const inputRect = input.getBoundingClientRect();
                const clickX = e.clientX - inputRect.left;
                
                // 检查是否有 onEnterEditing 回调（通过元素属性）
                const onEnterEditing = (input as any).__onEnterEditing;
                if (onEnterEditing && typeof onEnterEditing === 'function') {
                  // 先调用 onEnterEditing 回调（用于初始化本地输入值等）
                  onEnterEditing(input);
                }
                
                // 使用 activateFieldEdit 激活编辑，并传递 clickX
                activateFieldEdit({
                  element: input,
                  autoFocus: true,
                  selectText: false,
                  clickX,
                });
              } else {
                // 如果找不到输入框，直接聚焦 target（可能是下拉按钮）
                (target as HTMLElement).focus();
              }
            }, 0);
          });
        });
      } else {
        // 下拉按钮或其他元素，直接聚焦
        (target as HTMLElement).focus();
      }
    }
  }, [getPositionFromElement, setEditing, activateFieldEdit, scenesTableElementRefs]);

  // 处理鼠标进入事件
  const handleMouseEnter = useCallback((e: MouseEvent) => {
    const target = e.target;
    if (!target || !(target instanceof Element)) return;

    const position = getPositionFromElement(target as HTMLElement);
    if (position) {
      setHover(position);
    }
  }, [getPositionFromElement, setHover]);

  // 处理鼠标离开事件
  const handleMouseLeave = useCallback(() => {
    setHover(null);
  }, [setHover]);

  // 注册全局鼠标事件监听器
  useEffect(() => {
    if (!enabled) return;
    // 使用捕获阶段来提前拦截事件（特别是下拉按钮的点击）
    document.addEventListener('mousedown', handleMouseDown as (e: Event) => void, true);
    document.addEventListener('click', handleClick as (e: Event) => void, true);
    document.addEventListener('dblclick', handleDoubleClick as (e: Event) => void);
    document.addEventListener('mouseenter', handleMouseEnter as (e: Event) => void, true);
    document.addEventListener('mouseleave', handleMouseLeave as (e: Event) => void, true);

    return () => {
      document.removeEventListener('mousedown', handleMouseDown as (e: Event) => void, true);
      document.removeEventListener('click', handleClick as (e: Event) => void, true);
      document.removeEventListener('dblclick', handleDoubleClick as (e: Event) => void);
      document.removeEventListener('mouseenter', handleMouseEnter as (e: Event) => void, true);
      document.removeEventListener('mouseleave', handleMouseLeave as (e: Event) => void, true);

      // 清理定时器
      if (clickTimerRef.current) {
        clearTimeout(clickTimerRef.current);
      }
    };
  }, [enabled, handleMouseDown, handleClick, handleDoubleClick, handleMouseEnter, handleMouseLeave]);
}
